import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Download, ImageIcon, ImageOff, Paperclip, ShieldCheck } from "lucide-react";
import type { MailAttachment, MailMessage } from "../../shared/types";

interface MailMessageReaderProps {
  message: MailMessage;
  showHeaders?: boolean;
  onCopyCode: (code: string) => void;
  onDownloadAttachment: (attachment: MailAttachment) => void;
  onLoadInlineAttachment?: ((attachment: MailAttachment) => Promise<Blob>) | undefined;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function contentIdKey(value: string): string {
  return value.trim().replace(/^<|>$/g, "").toLowerCase();
}

function blobDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string"
      ? resolve(reader.result)
      : reject(new Error("无法读取内嵌图片"));
    reader.onerror = () => reject(reader.error ?? new Error("无法读取内嵌图片"));
    reader.readAsDataURL(blob);
  });
}

function resolveInlineImages(content: string, inlineUrls: Readonly<Record<string, string>>): string {
  return content.replace(/cid:([^\s"')>]+)/gi, (source, rawId: string) => {
    const resolved = inlineUrls[contentIdKey(rawId)];
    return resolved ?? source;
  });
}

function isolatedEmailDocument(
  message: MailMessage,
  allowRemoteImages: boolean,
  inlineUrls: Readonly<Record<string, string>>,
): string {
  const content = message.htmlBody.trim()
    ? resolveInlineImages(message.htmlBody, inlineUrls)
    : `<pre>${escapeHtml(message.textBody || "这封邮件没有可显示的正文。")}</pre>`;
  return `<!doctype html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8">
  <meta name="referrer" content="no-referrer">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: cid:${allowRemoteImages ? " https:" : ""}; style-src 'unsafe-inline'; font-src data:; connect-src 'none'; media-src data:; object-src 'none'; frame-src 'none'; form-action 'none'; base-uri 'none'">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <style>
    :root { color-scheme: light; }
    html, body { min-height: 100%; }
    body { margin: 0; padding: 24px; background: #fff; color: #201e1d; font: 14px/1.6 Arial, Helvetica, sans-serif; overflow-wrap: anywhere; }
    img { max-width: 100% !important; height: auto !important; }
    table { max-width: 100% !important; }
    pre { margin: 0; white-space: pre-wrap; font: inherit; }
    a { color: #ae1800; }
  </style>
</head>
<body>${content}</body>
</html>`;
}

function bytesLabel(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

export function MailMessageReader({
  message,
  showHeaders = false,
  onCopyCode,
  onDownloadAttachment,
  onLoadInlineAttachment,
}: MailMessageReaderProps) {
  const [view, setView] = useState<"rendered" | "text">(message.htmlBody.trim() ? "rendered" : "text");
  const [allowRemoteImages, setAllowRemoteImages] = useState(false);
  const [inlineUrls, setInlineUrls] = useState<Record<string, string>>({});
  const inlineLoader = useRef(onLoadInlineAttachment);
  useEffect(() => {
    inlineLoader.current = onLoadInlineAttachment;
  }, [onLoadInlineAttachment]);
  useEffect(() => {
    let disposed = false;
    const inlineAttachments = message.attachments.filter(
      (attachment) => attachment.contentId &&
        attachment.contentType.toLowerCase().startsWith("image/") &&
        attachment.sizeBytes <= 5 * 1024 * 1024,
    );
    if (!inlineLoader.current || inlineAttachments.length === 0) return undefined;
    void Promise.all(inlineAttachments.map(async (attachment) => {
      try {
        const blob = await inlineLoader.current?.(attachment);
        if (!blob) return null;
        return [contentIdKey(attachment.contentId ?? ""), await blobDataUrl(blob)] as const;
      } catch {
        return null;
      }
    })).then((entries) => {
      if (disposed) return;
      setInlineUrls(Object.fromEntries(entries.filter((entry) => entry !== null)));
    });
    return () => {
      disposed = true;
    };
  }, [message.attachments, message.id]);
  const hasRemoteImages = /(?:src\s*=\s*["']?\s*(?:https:)?\/\/|url\(\s*["']?\s*(?:https:)?\/\/)/i.test(message.htmlBody);
  const document = useMemo(
    () => isolatedEmailDocument(message, allowRemoteImages, inlineUrls),
    [allowRemoteImages, inlineUrls, message],
  );
  const headerEntries = Object.entries(message.headers);

  return (
    <article className="mail-reader">
      <section className="mail-reader__route">
        <div>
          <span className="eyebrow">发件人 · FROM</span>
          <strong>{message.senderName || message.senderAddress}</strong>
          {message.senderName ? <small>{message.senderAddress}</small> : null}
        </div>
        <dl>
          <div><dt>收件时间</dt><dd>{new Date(message.createdAt).toLocaleString("zh-CN", { month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}</dd></div>
          <div><dt>收件地址</dt><dd>{message.recipientAddress}</dd></div>
        </dl>
      </section>

      <section className="mail-reader__subject">
        <span className="eyebrow">邮件主题 · SUBJECT</span>
        <h3>{message.subject}</h3>
      </section>

      {message.verificationCode ? (
        <section className="mail-reader__code">
          <div><span className="eyebrow">自动提取 · VERIFICATION CODE</span><strong>检测到验证码</strong></div>
          <button type="button" onClick={() => onCopyCode(message.verificationCode ?? "")} aria-label={`复制验证码 ${message.verificationCode}`}>
            <code>{message.verificationCode}</code><Copy size={16} /><span>复制</span>
          </button>
        </section>
      ) : null}

      <section className="mail-reader__body">
        <header>
          <div><span className="eyebrow">邮件正文 · MESSAGE</span><strong>{view === "rendered" ? "原样阅读" : "纯文本阅读"}</strong></div>
          <div className="mail-reader__body-actions">
            {view === "rendered" && hasRemoteImages ? <button type="button" className={allowRemoteImages ? "mail-remote-images mail-remote-images--on" : "mail-remote-images"} onClick={() => setAllowRemoteImages((value) => !value)}>{allowRemoteImages ? <ImageOff size={13} /> : <ImageIcon size={13} />}{allowRemoteImages ? "停止加载外部图片" : "加载外部图片"}</button> : null}
            <div className="segments" aria-label="邮件正文显示方式">
              {message.htmlBody.trim() ? <button type="button" className={view === "rendered" ? "segment segment--active" : "segment"} onClick={() => setView("rendered")}>原样 <span>HTML</span></button> : null}
              <button type="button" className={view === "text" ? "segment segment--active" : "segment"} onClick={() => setView("text")}>纯文本 <span>TEXT</span></button>
            </div>
          </div>
        </header>
        {view === "rendered" && message.htmlBody.trim() ? (
          <iframe
            className="mail-reader__frame"
            title={`邮件正文：${message.subject}`}
            srcDoc={document}
            sandbox=""
            referrerPolicy="no-referrer"
          />
        ) : <pre className="mail-reader__text">{message.textBody || "这封邮件没有纯文本正文，请切换到原样阅读。"}</pre>}
      </section>

      {message.attachments.length ? (
        <section className="mail-reader__attachments">
          <header><Paperclip size={14} /><strong>附件</strong><span>{message.attachments.length} 个</span></header>
          <div>{message.attachments.map((attachment) => (
            <button type="button" key={attachment.id} onClick={() => onDownloadAttachment(attachment)}>
              <Download size={14} /><span><strong>{attachment.filename}</strong><small>{bytesLabel(attachment.sizeBytes)}</small></span>
            </button>
          ))}</div>
        </section>
      ) : null}

      {showHeaders && message.headersAvailable && headerEntries.length ? (
        <details className="mail-reader__headers">
          <summary>高级信息 · 原始信头 <span>RAW HEADERS</span></summary>
          <pre>{headerEntries.map(([key, value]) => `${key}: ${value}`).join("\n")}</pre>
        </details>
      ) : null}

      <footer className="mail-reader__safety"><ShieldCheck size={14} /><span>{allowRemoteImages ? "外部 HTTPS 图片已由你主动加载；脚本、表单、连接请求与来源标头仍保持禁用。" : "隔离显示：邮件脚本、表单与外部追踪图片默认禁用；需要时可在正文上方主动加载。"}</span></footer>
    </article>
  );
}
